import React, { useLayoutEffect } from 'react';
import { useOrderPersonalization } from '@/shared/hooks/useOrderPersonalization';
import { PersonalizationProvider } from '@/shared/contexts/PersonalizationContext';

interface OrderLayoutProps {
  children: React.ReactNode;
}

// Composant interne qui utilise le hook (doit être à l'intérieur du Provider)
const OrderLayoutContent: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const personalization = useOrderPersonalization();

  // Appliquer le thème au document.documentElement pour affecter aussi le Header/Sidebar
  useLayoutEffect(() => {
    const root = document.documentElement;
    const themeVars = getThemeVariables();

    // Sauvegarder les styles originaux pour pouvoir les restaurer (optionnel mais propre)
    const originalStyles: Record<string, string> = {};

    Object.entries(themeVars).forEach(([key, value]) => {
      originalStyles[key] = root.style.getPropertyValue(key);
      root.style.setProperty(key, value as string);
    });

    // Ajouter une classe spécifique pour identifier qu'on est en mode "Order Journey"
    root.classList.add('is-order-journey');

    return () => {
      // Restauration des styles originaux au démontage
      Object.entries(originalStyles).forEach(([key, value]) => {
        if (value) root.style.setProperty(key, value);
        else root.style.removeProperty(key);
      });
      root.classList.remove('is-order-journey');
    };
  }, [personalization]);

  // Helper pour obtenir le style du background
  const getBackgroundStyle = (): React.CSSProperties => {
    const bgType = personalization.layout.backgroundType;
    const bgValue = personalization.layout.background || '#FFFFFF';

    if (bgType === 'image') {
      return {
        backgroundImage: bgValue.startsWith('url(') ? bgValue : `url(${bgValue})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      };
    } else if (bgType === 'gradient') {
      return {
        background: bgValue,
      };
    } else {
      return {
        backgroundColor: bgValue,
      };
    }
  };

  // Calculer les variables CSS basées sur la personnalisation
  const getThemeVariables = () => {
    const vars: React.CSSProperties = {} as any;

    // Extraire la couleur primaire (gérer le cas où c'est un gradient)
    let primaryColor = personalization.button.primary || '#9333EA';
    if (primaryColor.includes('gradient') || primaryColor.includes('linear-gradient')) {
      // Extraire la première couleur du gradient pour les variables simples
      const match = primaryColor.match(/#[a-fA-F0-0]{6}|rgba?\([^)]+\)/);
      if (match) primaryColor = match[0];
      else primaryColor = '#9333EA'; // Fallback
    }

    // Définir les variables CSS pour Tailwind et les composants
    (vars as any)['--primary'] = primaryColor;
    (vars as any)['--primary-light'] = `${primaryColor}CC`;
    (vars as any)['--primary-dark'] = `${primaryColor}EE`;
    (vars as any)['--tw-ring-color'] = primaryColor;

    // Ajouter d'autres variables si nécessaire
    if (personalization.text.primary) (vars as any)['--foreground'] = personalization.text.primary;
    if (personalization.card.background) (vars as any)['--surface'] = personalization.card.background;
    if (personalization.card.border) (vars as any)['--border'] = personalization.card.border;

    return vars;
  };

  return (
    <div style={getThemeVariables()} className="min-h-screen">
      {/* Background fixe qui couvre toute la page */}
      <div className="fixed inset-0 w-full h-full" style={{ ...getBackgroundStyle(), zIndex: 0 }} />
      {/* Contenu principal avec scroll */}
      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8 py-8 mt-12">
        {children}
      </div>
    </div>
  );
};

const OrderLayout: React.FC<OrderLayoutProps> = ({ children }) => {
  return (
    <PersonalizationProvider>
      <OrderLayoutContent>
        {children}
      </OrderLayoutContent>
    </PersonalizationProvider>
  );
};

export default OrderLayout;
